import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Icon, IconButton, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import {
  cloneJobTaskVersionRequirements,
  getJobTaskVersionRequirements,
  replaceJobTaskVersionRequirements,
} from 'http/jobTaskVersions';
import {
  getCompanyJobRoleVersionRequirements,
  replaceCompanyJobRoleVersionRequirements,
} from 'http/companyProvisioning';
import {
  getProvisionedJobRoleVersionRequirements,
  replaceProvisionedJobRoleVersionRequirements,
  getProvisionedTrainingModulesList,
} from 'http/hyperAdminProvisioned';
import { getTrainingModulesList } from 'http/trainingModules';
import CustomSelect from 'components/Forms/CustomSelect';
import SubmitErrorMessage from 'components/Forms/SubmitErrorMessage';
import { resolveApiErrorMessage } from 'utils';

const LOCKED_STATUSES = ['active', 'retired'];

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '8px',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
    },
  },
  toolbarGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  },
  toolbarGroupPrimary: {
    flex: '1 1 auto',
    [theme.breakpoints.up('sm')]: {
      flex: '1 1 0',
      maxWidth: 520,
    },
  },
  toolbarGroupSecondary: {
    flex: '1 1 auto',
    [theme.breakpoints.up('sm')]: {
      flex: '0 1 auto',
      marginLeft: 'auto',
      maxWidth: 420,
    },
  },
  selectField: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  actionButton: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  moduleCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  moduleHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  moduleTitle: {
    fontWeight: 600,
  },
  actions: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'flex-end',
    '& > * + *': {
      marginLeft: theme.spacing(1.5),
    },
  },
}));

const buildEmptyModule = (trainingModule, sequence) => ({
  trainingModuleId: trainingModule.id,
  trainingModuleCode: trainingModule.code,
  trainingModuleName: trainingModule.name,
  status: 'active',
  sequence,
});

const JobTaskVersionRequirementsEditor = ({
  version,
  allVersions,
  onCancel,
  onSaved,
  layout,
  apiMode = 'system',
  jobRoleId,
  provisionedCompanyId = null,
  isReadOnly = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const [modules, setModules] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [cloneSourceId, setCloneSourceId] = useState('');

  const isLocked = isReadOnly || LOCKED_STATUSES.includes(version?.status);
  const isCompanyApi = apiMode === 'company';
  const isProvisionedApi = apiMode === 'provisioned';

  const fetchRequirements = useCallback(async () => {
    if (isProvisionedApi) {
      return getProvisionedJobRoleVersionRequirements(provisionedCompanyId, jobRoleId, version.id);
    }
    if (isCompanyApi) {
      return getCompanyJobRoleVersionRequirements(jobRoleId, version.id);
    }
    return getJobTaskVersionRequirements(version.id);
  }, [isCompanyApi, isProvisionedApi, jobRoleId, provisionedCompanyId, version.id]);

  const persistRequirements = useCallback(
    async payload => {
      if (isProvisionedApi) {
        return replaceProvisionedJobRoleVersionRequirements(provisionedCompanyId, jobRoleId, version.id, payload);
      }
      if (isCompanyApi) {
        return replaceCompanyJobRoleVersionRequirements(jobRoleId, version.id, payload);
      }
      return replaceJobTaskVersionRequirements(version.id, payload);
    },
    [isCompanyApi, isProvisionedApi, jobRoleId, provisionedCompanyId, version.id],
  );

  const cloneRequirementsFromVersion = useCallback(
    async sourceVersionId => {
      if (isProvisionedApi) {
        return getProvisionedJobRoleVersionRequirements(provisionedCompanyId, jobRoleId, sourceVersionId);
      }
      if (isCompanyApi) {
        return getCompanyJobRoleVersionRequirements(jobRoleId, sourceVersionId);
      }
      return cloneJobTaskVersionRequirements(version.id, sourceVersionId);
    },
    [isCompanyApi, isProvisionedApi, jobRoleId, provisionedCompanyId, version.id],
  );
  const cloneCandidates = useMemo(
    () => (allVersions || []).filter(item => item.id !== version.id),
    [allVersions, version.id],
  );

  const [loadState, loadRequirements] = useAsyncFn(async () => {
    const trainingModulesPromise = isProvisionedApi
      ? getProvisionedTrainingModulesList(provisionedCompanyId, { page: 0, pageSize: 200 })
      : getTrainingModulesList({ page: 0, pageSize: 200 });

    const [requirementsResult, trainingModulesResult] = await Promise.all([
      fetchRequirements(),
      trainingModulesPromise,
    ]);

    if (requirementsResult?.error || requirementsResult?.statusCode) {
      return requirementsResult;
    }

    if (!trainingModulesResult?.error && !trainingModulesResult?.statusCode) {
      setAvailableModules(trainingModulesResult.items || []);
    }

    setModules(requirementsResult.modules || []);
    return requirementsResult;
  }, [fetchRequirements, isProvisionedApi, provisionedCompanyId]);

  useEffect(() => {
    loadRequirements();
  }, [loadRequirements]);

  const [saveState, saveRequirements] = useAsyncFn(async () => {
    setSubmitError('');
    const payload = {
      modules: modules.map((module, index) => ({
        trainingModuleId: module.trainingModuleId,
        status: module.status || 'active',
        sequence: module.sequence ?? index + 1,
      })),
    };

    const result = await persistRequirements(payload);
    if (result?.error || result?.statusCode) {
      setSubmitError(
        resolveApiErrorMessage(result.message, {
          t,
          fallbackMessage: t('errors.serverError'),
        }),
      );
      return result;
    }

    setModules(result.modules || []);
    if (onSaved) {
      onSaved(result);
    }
    onCancel();
    return result;
  }, [modules, onCancel, onSaved, persistRequirements, t]);

  const [cloneState, cloneRequirements] = useAsyncFn(
    async sourceVersionId => {
      setSubmitError('');
      const result = await cloneRequirementsFromVersion(sourceVersionId);
      if (result?.error || result?.statusCode) {
        setSubmitError(
          resolveApiErrorMessage(result.message, {
            t,
            fallbackMessage: t('errors.serverError'),
          }),
        );
        return result;
      }

      setModules(result.modules || []);
      return result;
    },
    [cloneRequirementsFromVersion, t],
  );

  const moduleOptions = useMemo(() => {
    const addedIds = new Set(modules.map(item => String(item.trainingModuleId)));
    return availableModules
      .filter(item => !addedIds.has(String(item.id)))
      .map(item => ({
        value: String(item.id),
        label: `${item.code} — ${item.name}`,
      }));
  }, [availableModules, modules]);

  const selectedModuleOption = useMemo(
    () => moduleOptions.find(option => option.value === String(selectedModuleId)) || null,
    [moduleOptions, selectedModuleId],
  );

  const cloneOptions = useMemo(
    () =>
      cloneCandidates.map(item => ({
        value: String(item.id),
        label: item.versionCode + '.' + item.revision,
      })),
    [cloneCandidates],
  );

  const selectedCloneOption = useMemo(
    () => cloneOptions.find(option => option.value === String(cloneSourceId)) || null,
    [cloneOptions, cloneSourceId],
  );

  const handleAddModule = () => {
    const trainingModule = availableModules.find(item => String(item.id) === String(selectedModuleId));
    if (!trainingModule) {
      return;
    }

    if (modules.some(item => String(item.trainingModuleId) === String(trainingModule.id))) {
      setSubmitError(t('jobTaskVersionRequirements.duplicateTrainingModule'));
      return;
    }

    setSubmitError('');
    setModules(current => [...current, buildEmptyModule(trainingModule, current.length + 1)]);
    setSelectedModuleId('');
  };

  const handleRemoveModule = index => {
    setModules(current => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const isBusy = loadState.loading || saveState.loading || cloneState.loading;

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {t('jobTaskVersionRequirements.versionSummary', {
          versionCode: version.versionCode,
          status: t(`jobTaskVersions.statuses.${version.status}`),
        })}
      </Typography>

      {isLocked && (
        <Typography variant="body2" color="error" gutterBottom>
          {t('jobTaskVersionRequirements.lockedHint')}
        </Typography>
      )}

      {!isLocked && (
        <Box className={classes.toolbar}>
          <Box className={`${classes.toolbarGroup} ${classes.toolbarGroupPrimary}`}>
            <Box className={classes.selectField}>
              <CustomSelect
                input={{
                  name: 'addTrainingModule',
                  type: 'text',
                  value: selectedModuleOption,
                  onChange: option => setSelectedModuleId(option?.value || ''),
                  onBlur: () => {},
                  onFocus: () => {},
                }}
                meta={{}}
                id="addTrainingModule"
                placeholder={t('jobTaskVersionRequirements.selectModulePlaceholder')}
                options={moduleOptions}
                isDisabled={isBusy}
                isClearable
              />
            </Box>
            <Button
              className={classes.actionButton}
              variant="contained"
              color="primary"
              startIcon={<Icon>add</Icon>}
              onClick={handleAddModule}
              disabled={!selectedModuleId || isBusy}
              size="large"
            >
              {t('jobTaskVersionRequirements.actionAddModule')}
            </Button>
          </Box>

          {cloneCandidates.length > 0 && (
            <Box className={`${classes.toolbarGroup} ${classes.toolbarGroupSecondary}`}>
              <Box className={classes.selectField}>
                <CustomSelect
                  input={{
                    name: 'cloneFromVersion',
                    type: 'text',
                    value: selectedCloneOption,
                    onChange: option => setCloneSourceId(option?.value || ''),
                    onBlur: () => {},
                    onFocus: () => {},
                  }}
                  meta={{}}
                  id="cloneFromVersion"
                  placeholder={t('jobTaskVersionRequirements.cloneFromLabel')}
                  options={cloneOptions}
                  isDisabled={isBusy}
                  isClearable
                />
              </Box>
              <Button
                className={classes.actionButton}
                variant="outlined"
                onClick={() => cloneRequirements(Number(cloneSourceId))}
                disabled={!cloneSourceId || isBusy}
                size="large"
              >
                {t('jobTaskVersionRequirements.actionClone')}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {modules.map((module, moduleIndex) => (
        <Paper key={`${module.trainingModuleId}-${moduleIndex}`} variant="outlined" className={classes.moduleCard}>
          <Box className={classes.moduleHeader}>
            <Box>
              <Typography variant="subtitle1" className={classes.moduleTitle}>
                {module.trainingModuleCode || module.trainingModuleId}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {module.trainingModuleName}
              </Typography>
            </Box>
            {!isLocked && (
              <IconButton
                aria-label={t('jobTaskVersionRequirements.removeModule')}
                onClick={() => handleRemoveModule(moduleIndex)}
                disabled={isBusy}
              >
                <Icon>delete</Icon>
              </IconButton>
            )}
          </Box>
        </Paper>
      ))}

      {submitError && <SubmitErrorMessage message={submitError} />}

      <Box className={classes.actions}>
        <Button variant="outlined" onClick={onCancel} disabled={isBusy}>
          {t(layout === 'page' ? 'buttons.back' : 'buttons.cancel')}
        </Button>
        {!isLocked && (
          <Button color="primary" variant="contained" onClick={saveRequirements} disabled={isBusy}>
            {t('buttons.save')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

JobTaskVersionRequirementsEditor.propTypes = {
  version: PropTypes.shape({
    id: PropTypes.number.isRequired,
    versionCode: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  allVersions: PropTypes.arrayOf(PropTypes.object),
  onCancel: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  layout: PropTypes.oneOf(['modal', 'page']),
  apiMode: PropTypes.oneOf(['system', 'company', 'provisioned']),
  jobRoleId: PropTypes.number,
  provisionedCompanyId: PropTypes.number,
  isReadOnly: PropTypes.bool,
};

JobTaskVersionRequirementsEditor.defaultProps = {
  allVersions: [],
  onSaved: null,
  layout: 'modal',
  apiMode: 'system',
  jobRoleId: null,
  provisionedCompanyId: null,
  isReadOnly: false,
};

export default JobTaskVersionRequirementsEditor;
